import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function MenuSection({ section, env }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/menu", { params: { env } }).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [env]);

  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((i) => {
      const c = i.category || "Other";
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(i);
    });
    return [...map.entries()];
  }, [items]);
  const subtitles = section.category_subtitles || {};

  return (
    <section className="mx-auto max-w-6xl px-6 sm:px-12 lg:px-20 py-24 sm:py-28" data-testid="menu-section">
      <p className="text-xs uppercase font-mono tracking-[0.3em] text-gold">{section.eyebrow}</p>
      <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-ink tracking-tight">{section.title}</h2>
      <nav className="mt-10 flex flex-wrap gap-3" data-testid="menu-category-tabs">
        {groups.map(([cat]) => (
          <a
            key={cat}
            href={`#menu-${slugify(cat)}`}
            data-testid={`menu-tab-${slugify(cat)}`}
            className="rounded-full border border-brass/30 px-5 py-2 text-xs font-semibold uppercase tracking-widest text-ink2 transition-colors hover:bg-brass hover:text-cast hover:border-brass"
          >
            {cat}
          </a>
        ))}
      </nav>
      {groups.map(([cat, catItems], gi) => (
        <div key={cat} id={`menu-${slugify(cat)}`} className="mt-16 sm:mt-20 scroll-mt-28" data-testid={`menu-group-${slugify(cat)}`}>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="font-mono text-sm text-flame/80">{String(gi + 1).padStart(2, "0")} /</p>
            <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-ink">{cat}</h3>
            {subtitles[cat] && <p className="mt-1.5 text-xs font-mono uppercase tracking-[0.25em] text-gold">{subtitles[cat]}</p>}
          </motion.div>
          <div className="mt-8 grid gap-x-14 gap-y-8 md:grid-cols-2">
            {catItems.map((item, i) => (
              <motion.article
                key={item.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 2) * 0.08 }}
                className={item.image ? "md:col-span-2 flex flex-col sm:flex-row gap-6 items-start rounded-sm border border-brass/20 bg-parchment/60 p-5" : ""}
                data-testid={`menu-item-${item.id}`}
              >
                {item.image && (
                  <img src={item.image} alt={item.name} className="w-full sm:w-56 shrink-0 aspect-[16/10] object-cover rounded-sm max-h-[80vh]" />
                )}
                <div className="flex-1 w-full">
                  <div className="flex items-baseline gap-3">
                    <h4 className="font-serif text-lg text-ink">{item.name}</h4>
                    <span className="flex-1 border-b border-dotted border-ink/25" />
                    {item.price && <span className="font-mono text-sm text-gold whitespace-nowrap">{item.price}</span>}
                  </div>
                  <p className="mt-1.5 text-sm text-ink2 leading-relaxed">{item.description}</p>
                  {item.image && <p className="mt-3 text-[10px] font-mono uppercase tracking-[0.25em] text-flame/80">Signature</p>}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
